import { Link } from "react-router-dom";
import { Twitter, Linkedin, Facebook, Youtube, Mail } from "lucide-react";
import Logo from "@/components/site/Logo";
import { NAV } from "@/components/site/Header";
import { useSite } from "@/lib/site";

const LEGAL = [
  { to: "/legal/privacy-policy", label: "Privacy Policy" },
  { to: "/legal/terms-of-use", label: "Terms of Use" },
  { to: "/legal/cookie-policy", label: "Cookie Policy" },
  { to: "/legal/affiliate-disclosure", label: "Affiliate Disclosure" },
  { to: "/legal/editorial-policy", label: "Editorial Policy" },
];
const SOCIAL = [["twitter_url", Twitter, "X / Twitter"], ["linkedin_url", Linkedin, "LinkedIn"], ["facebook_url", Facebook, "Facebook"], ["youtube_url", Youtube, "YouTube"]];

const Col = ({ title, children }) => (
  <div>
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] !text-[#8FA0B5] mb-5">{title}</h3>
    <ul className="space-y-3 text-[14px]">{children}</ul>
  </div>
);
const fl = "text-[#C9D2DD] hover:text-white transition-colors";

export default function Footer() {
  const { categories, settings } = useSite();
  const socials = SOCIAL.filter(([k]) => settings.social?.[k]);
  return (
    <footer data-testid="site-footer" className="footer-bg text-[#C9D2DD] mt-8">
      <div className="container-x pt-16 pb-12 md:pt-20 grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1.2fr_1.2fr]">
        <div className="space-y-5 max-w-sm sm:col-span-2 lg:col-span-1">
          <Logo light testId="footer-logo-link" />
          <p className="text-[14px] leading-relaxed text-[#C9D2DD]">
            An independent software discovery and research platform publishing guides, reviews and comparisons for businesses.
          </p>
          <p className="font-display text-sm font-semibold text-[#8FD3C8]">Discover Smarter. Choose Better.</p>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-xs text-[#AEB9C7] leading-relaxed">
            SaaSPivora may earn commissions from qualifying referrals. This never automatically determines our editorial recommendations.{" "}
            <Link to="/legal/affiliate-disclosure" className="text-white underline underline-offset-2" data-testid="footer-affiliate-note-link">Learn more</Link>
          </div>
          {socials.length > 0 && (
            <div className="flex gap-2 pt-1">
              {socials.map(([k, Icon, label]) => (
                <a key={k} href={settings.social[k]} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${k.replace("_url", "")}`}
                  className="h-9 w-9 rounded-full border border-white/15 grid place-items-center hover:bg-[#0F766E] hover:border-[#0F766E] transition-colors"><Icon className="h-4 w-4" /></a>
              ))}
            </div>
          )}
        </div>
        <Col title="Explore">
          {NAV.map((n) => <li key={n.id}><Link to={n.to} className={fl} data-testid={`footer-nav-${n.id}`}>{n.label}</Link></li>)}
        </Col>
        <Col title="Categories">
          {categories.slice(0, 6).map((c) => <li key={c.slug}><Link to={`/categories/${c.slug}`} className={fl} data-testid={`footer-cat-${c.slug}`}>{c.name}</Link></li>)}
          <li><Link to="/categories" className="text-[#8FD3C8] hover:text-white transition-colors font-medium" data-testid="footer-all-categories">All categories →</Link></li>
        </Col>
        <Col title="Get in Touch">
          {settings.public_email && (
            <li><a href={`mailto:${settings.public_email}`} className="inline-flex items-center gap-2 text-white font-medium hover:text-[#8FD3C8] transition-colors" data-testid="footer-public-email"><Mail className="h-4 w-4 text-[#8FD3C8]" />{settings.public_email}</a></li>
          )}
          <li><Link to="/contact" className={fl} data-testid="footer-contact-page-link">Contact form</Link></li>
          <li className="pt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8FA0B5]">Legal</li>
          {LEGAL.map((l) => <li key={l.to}><Link to={l.to} className={fl} data-testid={`footer-${l.to.split("/").pop()}`}>{l.label}</Link></li>)}
          {settings.contact_email && settings.contact_email !== settings.public_email && (
            <li className="pt-3 mt-1 border-t border-white/10"><a href={`mailto:${settings.contact_email}`} className={fl} data-testid="footer-contact-email">{settings.contact_email}</a></li>
          )}
        </Col>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 text-xs text-[#8FA0B5] flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} SaaSPivora. All rights reserved.</span>
          <span>Product names and logos are trademarks of their respective owners.</span>
        </div>
      </div>
    </footer>
  );
}
